import { get } from 'lodash';
import { useEffect, useState, useRef } from 'react';
import {
  Avatar, Box, Button, Card, CardContent, Chip, Container, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, OutlinedInput, Select,
  Stack, Switch, TextField, Typography, FormControlLabel,
} from '@mui/material';
import { Icon } from '@iconify/react';
import Page from '../../components/Page';
import config from '../../util/config';
import { createTeacher, getAllTeachersAdmin, deleteTeacherAdmin } from '../../axiosLib/services/teacher/admin';
import { getAllCoursesAdmin } from '../../axiosLib/services/course/admin';
import { adminLevel } from '../../axiosLib/services/institution/admin';

const EMPTY_TEACHER = {
  name: '', designation: '', institution: '', subjects: '', phone: '', email: '',
  bio: '', image: '', imageBase64: '', isActive: true, courseIds: [],
};

export default function TeacherEnrollment() {
  const [teachers, setTeachers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [teacherData, setTeacherData] = useState(EMPTY_TEACHER);
  const [preview, setPreview] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const fileRef = useRef(null);

  const fetchTeachers = () => {
    getAllTeachersAdmin((error, data) => {
      if (!error) setTeachers(get(data, 'teachers', []));
    });
  };

  const fetchCourses = () => {
    getAllCoursesAdmin((error, data) => {
      if (!error) setCourses(get(data, 'courses', []));
    });
  };

  useEffect(() => {
    fetchTeachers();
    fetchCourses();
    adminLevel((error, data) => {
      if (!error) {
        const roles = get(data, 'admin.roleSet', []);
        setCanManage(roles.includes('TEACHER_MANAGEMENT'));
      }
    });
  }, []);

  const handleChange = (field, e) => {
    setTeacherData({ ...teacherData, [field]: e.target.value });
  };

  const handleImagePick = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
        setTeacherData({ ...teacherData, imageBase64: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    createTeacher(teacherData, (error, data) => {
      if (!error) {
        setModalOpen(false);
        setTeacherData(EMPTY_TEACHER);
        setPreview('');
        fetchTeachers();
      } else {
        alert('Error saving teacher');
      }
    });
  };

  const handleEdit = (teacher) => {
    setTeacherData({
      ...teacher,
      courseIds: get(teacher, 'courseIds', []).map(c => typeof c === 'object' ? c._id : c),
      imageBase64: '',
    });
    setPreview(teacher.image ? config.BASE_API_URL + teacher.image : '');
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    deleteTeacherAdmin(id, (error) => {
      if (!error) {
        setDeleteConfirm(null);
        fetchTeachers();
      }
    });
  };

  const handleAddNew = () => {
    setTeacherData(EMPTY_TEACHER);
    setPreview('');
    setModalOpen(true);
  };

  return (
    <Page title="Teacher Management">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e1b4b' }}>
              শিক্ষক মণ্ডলী
            </Typography>
            <Typography variant="body2" sx={{ color: '#888' }}>
              Teacher Management — Add, edit, and manage teacher profiles
            </Typography>
          </Box>
          {canManage && (
            <Button variant="contained" startIcon={<Icon icon="eva:plus-fill" />}
              onClick={handleAddNew}
              sx={{
                background: 'linear-gradient(135deg, #4338ca, #6366f1)',
                fontWeight: 700, borderRadius: 2, textTransform: 'none', px: 3,
                '&:hover': { background: 'linear-gradient(135deg, #3730a3, #4f46e5)' },
              }}>
              Add Teacher
            </Button>
          )}
        </Stack>

        {/* Teacher Cards Grid */}
        <Grid container spacing={3}>
          {teachers.map((teacher) => (
            <Grid item xs={12} sm={6} md={4} key={teacher._id}>
              <Card sx={{
                borderRadius: 3, overflow: 'hidden', transition: 'all 0.3s',
                border: '1px solid rgba(99,102,241,0.08)',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(99,102,241,0.12)' },
              }}>
                {/* Image header */}
                <Box sx={{
                  height: 200, position: 'relative',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Avatar
                    src={teacher.image ? config.BASE_API_URL + teacher.image : ''}
                    sx={{
                      width: 120, height: 120, border: '4px solid rgba(180,140,60,0.2)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                    }}
                  />
                  <Chip size="small"
                    label={teacher.isActive ? 'সক্রিয়' : 'নিষ্ক্রিয়'}
                    sx={{
                      position: 'absolute', top: 12, right: 12,
                      background: teacher.isActive ? '#22c55e' : '#ef4444',
                      color: '#fff', fontWeight: 700, fontSize: '0.7rem',
                    }}
                  />
                </Box>

                <CardContent sx={{ p: 3 }}>
                  <Typography sx={{ fontWeight: 800, color: '#1e1b4b', fontSize: '1.1rem', mb: 0.5 }}>
                    {teacher.name}
                  </Typography>
                  {teacher.designation && (
                    <Typography sx={{ color: '#6366f1', fontWeight: 600, fontSize: '0.82rem', mb: 1 }}>
                      {teacher.designation}
                    </Typography>
                  )}
                  {teacher.institution && (
                    <Typography sx={{ color: '#888', fontSize: '0.8rem', mb: 1 }}>
                      🏫 {teacher.institution}
                    </Typography>
                  )}
                  {teacher.subjects && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                      {teacher.subjects.split(',').map((s, i) => (
                        <Chip key={i} label={s.trim()} size="small"
                          sx={{
                            background: 'rgba(99,102,241,0.08)', color: '#4338ca',
                            fontWeight: 600, fontSize: '0.7rem',
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                  {teacher.phone && (
                    <Typography sx={{ color: '#666', fontSize: '0.78rem' }}>
                      📞 {teacher.phone}
                    </Typography>
                  )}

                  {/* Assigned courses */}
                  {get(teacher, 'courseIds', []).length > 0 && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                      <Typography sx={{ color: '#888', fontSize: '0.72rem', fontWeight: 700, mb: 0.5 }}>
                        COURSES
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {teacher.courseIds.map((c, i) => (
                          <Chip key={i} label={typeof c === 'object' ? c.title : c} size="small"
                            sx={{ background: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '0.68rem' }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Actions */}
                  {canManage && (
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                      <Button size="small" variant="outlined" startIcon={<Icon icon="mdi:pencil" />}
                        onClick={() => handleEdit(teacher)}
                        sx={{
                          flex: 1, textTransform: 'none', fontWeight: 600,
                          borderColor: 'rgba(99,102,241,0.3)', color: '#4338ca',
                          '&:hover': { borderColor: '#4338ca', background: 'rgba(99,102,241,0.04)' },
                        }}>
                        Edit
                      </Button>
                      <Button size="small" variant="outlined" color="error"
                        startIcon={<Icon icon="mdi:delete-outline" />}
                        onClick={() => setDeleteConfirm(teacher._id)}
                        sx={{ textTransform: 'none', fontWeight: 600 }}>
                        Delete
                      </Button>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}

          {teachers.length === 0 && (
            <Grid item xs={12}>
              <Box sx={{
                textAlign: 'center', py: 8, borderRadius: 3,
                background: 'rgba(99,102,241,0.03)', border: '2px dashed rgba(99,102,241,0.15)',
              }}>
                <Icon icon="mdi:account-group-outline" style={{ fontSize: 64, color: '#c7d2fe' }} />
                <Typography sx={{ color: '#888', mt: 2, fontWeight: 600 }}>
                  No teachers added yet. Click "Add Teacher" to get started.
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* ═══════ ADD/EDIT TEACHER MODAL ═══════ */}
        <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="md">
          <DialogTitle sx={{ fontWeight: 800, color: '#1e1b4b', pb: 0 }}>
            {teacherData._id ? 'Edit Teacher' : 'Add New Teacher'}
          </DialogTitle>
          <DialogContent sx={{ pt: '16px !important' }}>
            <Stack spacing={3}>
              {/* Image upload */}
              <Box sx={{ textAlign: 'center' }}>
                <input type="file" accept="image/*" ref={fileRef}
                  style={{ display: 'none' }} onChange={handleImagePick} />
                <Avatar src={preview}
                  sx={{
                    width: 120, height: 120, mx: 'auto', mb: 1, cursor: 'pointer',
                    border: '3px solid rgba(99,102,241,0.2)',
                    '&:hover': { opacity: 0.8 },
                  }}
                  onClick={() => fileRef.current.click()}
                />
                <Typography variant="caption" sx={{ color: '#888' }}>
                  Click avatar to upload photo
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField fullWidth label="Teacher Name *" value={get(teacherData, 'name', '')}
                  onChange={(e) => handleChange('name', e)} />
                <TextField fullWidth label="Designation" value={get(teacherData, 'designation', '')}
                  onChange={(e) => handleChange('designation', e)}
                  placeholder="e.g., Senior Lecturer" />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField fullWidth label="Institution" value={get(teacherData, 'institution', '')}
                  onChange={(e) => handleChange('institution', e)}
                  placeholder="e.g., Dhaka Medical College" />
                <TextField fullWidth label="Subjects (comma separated)" value={get(teacherData, 'subjects', '')}
                  onChange={(e) => handleChange('subjects', e)}
                  placeholder="e.g., Biology, Physics" />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField fullWidth label="Phone" value={get(teacherData, 'phone', '')}
                  onChange={(e) => handleChange('phone', e)} />
                <TextField fullWidth label="Email" value={get(teacherData, 'email', '')}
                  onChange={(e) => handleChange('email', e)} />
              </Stack>

              <TextField fullWidth label="Bio / Description" multiline rows={3}
                value={get(teacherData, 'bio', '')}
                onChange={(e) => handleChange('bio', e)} />

              {/* Course assignment */}
              <FormControl fullWidth>
                <InputLabel>Assign to Courses</InputLabel>
                <Select multiple
                  value={get(teacherData, 'courseIds', [])}
                  onChange={(e) => setTeacherData({ ...teacherData, courseIds: e.target.value })}
                  input={<OutlinedInput label="Assign to Courses" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => {
                        const course = courses.find(c => c._id === id);
                        return <Chip key={id} label={course ? course.title : id} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {courses.map((course) => (
                    <MenuItem key={course._id} value={course._id}>
                      {course.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch checked={get(teacherData, 'isActive', true)}
                    onChange={(e) => setTeacherData({ ...teacherData, isActive: e.target.checked })}
                    color="primary" />
                }
                label="Active"
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={() => setModalOpen(false)}
              sx={{ textTransform: 'none', color: '#888' }}>Cancel</Button>
            <Button variant="contained" onClick={handleSave}
              sx={{
                background: 'linear-gradient(135deg, #4338ca, #6366f1)',
                fontWeight: 700, textTransform: 'none', px: 4,
                '&:hover': { background: 'linear-gradient(135deg, #3730a3, #4f46e5)' },
              }}>
              {teacherData._id ? 'Update Teacher' : 'Add Teacher'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirmation */}
        <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this teacher? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirm(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirm)}
              sx={{ textTransform: 'none' }}>Delete</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
